import { useEffect, useState } from 'react';
import dashboardService from '../../../services/DashboardService.js';
import { hasShipmentFeatureAccess } from '../../../utils/networkMode.js';
import { fullCurrency } from '../../../utils/formatCurrency.js';
import ReturnBreakdownModal from './ReturnBreakdownModal.js';

const money = (value) => fullCurrency(value || 0);
const sumRows = (rows, key) => (rows || []).reduce((total, row) => total + Number(row[key] || 0), 0);

/**
 * Agent return table. Loads on its own so the rest of the dashboard does not wait.
 * An agent only receives their own row from the API.
 */
const AgentRemittanceSection = ({
  user,
  dateRange = 'all_time',
  customStart = '',
  customEnd = '',
}) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openUserId, setOpenUserId] = useState(null);
  const isAgent = user?.role === 'Agent';
  const hidden = !user || user.network_mode === 'EXTERNAL' || !hasShipmentFeatureAccess(user);

  useEffect(() => {
    if (hidden) return undefined;
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = { date_range: dateRange || 'all_time' };
    if (dateRange === 'custom') {
      params.from_date = customStart;
      params.to_date = customEnd;
    }
    dashboardService.getAgentRemittance(params)
      .then((res) => {
        if (!cancelled) setRows(res?.data?.agents || []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [hidden, user?.user_id, user?.company_id, dateRange, customStart, customEnd]);

  if (hidden) return null;
  if (!loading && Array.isArray(rows) && rows.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800">
          {isAgent ? 'Your return to company' : 'Agent returns'}
        </h2>
      </div>
      {loading && !rows ? (
        <p className="px-4 py-6 text-sm text-gray-400">Loading agent returns…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {!isAgent ? <th className="text-left px-3 py-2">Agent</th> : null}
                <th className="text-right px-3 py-2">Paid</th>
                <th className="text-right px-3 py-2">Booking commission</th>
                <th className="text-right px-3 py-2">Loading</th>
                <th className="text-right px-3 py-2">To-Pay</th>
                <th className="text-right px-3 py-2">Delivery commission</th>
                <th className="text-right px-3 py-2">Unloading</th>
                <th className="text-right px-3 py-2">Returned</th>
                <th className="text-right px-3 py-2">Still due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(rows || []).map((row) => (
                <tr
                  key={row.user_id}
                  onClick={() => setOpenUserId(row.user_id)}
                  className="cursor-pointer hover:bg-sky-50/70"
                >
                  {!isAgent ? <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td> : null}
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.paid_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.booking_commission)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.loading)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.topay_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.delivery_commission)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.unloading)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(row.returned)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.still_due)}</td>
                </tr>
              ))}
            </tbody>
            {!isAgent && (rows || []).length ? (
              <tfoot>
                <tr className="bg-gray-50 font-semibold text-gray-900">
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'paid_total'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'booking_commission'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'loading'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'topay_total'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'delivery_commission'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'unloading'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(sumRows(rows, 'returned'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'still_due'))}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
      {isAgent && (rows || []).length ? (
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 text-sm font-semibold text-gray-900 flex justify-between">
          <span>Total</span>
          <span className="tabular-nums">Still due {money(sumRows(rows, 'still_due'))}</span>
        </div>
      ) : null}
      {openUserId ? (
        <ReturnBreakdownModal
          kind="agent"
          userId={openUserId}
          dateRange={dateRange}
          customStart={customStart}
          customEnd={customEnd}
          onClose={() => setOpenUserId(null)}
        />
      ) : null}
    </div>
  );
};

export default AgentRemittanceSection;
